import { Capacitor } from '@capacitor/core';
import { clearOfflineServiceWorkerUpdatePending } from '@core/domain/offlineServiceWorkerUpdateEvents.ts';
import { ok, fail, type Result } from '@core/domain/utils/Result.ts';
import type { AuthTokenStoragePort } from '@src/modules/auth/domain/ports/AuthTokenStoragePort.ts';
import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import {
  clearAppMaintenanceLocalStorageKeys,
  clearAppSessionStorageKeys,
  clearAuthTokenFromBrowserStorage,
} from '@src/modules/shared/local-sqlite/infrastructure/appClientStorageMaintenance.ts';
import { deleteLocalPuntosEntregaIdb } from '@src/modules/shared/local-sqlite/infrastructure/localSqliteIdbMaintenance.ts';
import { getEmbeddedSnapshotRevision } from '@src/modules/shared/local-sqlite/infrastructure/snapshotRevisionGuard.ts';
import type { AppRuntimeInfo } from '../../domain/appUpdateManifest.ts';
import type {
  AppMaintenancePort,
  AppMaintenanceResetOptions,
} from '../../domain/ports/AppMaintenancePort.ts';
import { clearBrowserOfflineShellCaches } from './browserOfflineCacheMaintenance.ts';
import { BonoNativeMaintenance } from './capacitor/bonoNativeMaintenanceBridge.ts';

export type CreateAppMaintenancePortDeps = {
  localSqlite?: LocalSqliteRuntime;
  tokenStorage?: AuthTokenStoragePort;
};

/** Browser + Capacitor implementation for cache wipe and optional SQLite reset. */
export function createAppMaintenancePort(deps: CreateAppMaintenancePortDeps = {}): AppMaintenancePort {
  return {
    async getRuntimeInfo(): Promise<AppRuntimeInfo> {
      const buildRevision = appConfigEnvBuildRevision();
      const base: AppRuntimeInfo = {
        platform: Capacitor.isNativePlatform() ? 'native' : 'web',
        buildRevision,
        snapshotRevision: getEmbeddedSnapshotRevision(),
      };
      if (!Capacitor.isNativePlatform()) return base;
      try {
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        const code = Number.parseInt(String(info.build ?? ''), 10);
        return {
          ...base,
          appVersionName: info.version,
          appVersionCode: Number.isFinite(code) ? code : undefined,
        };
      } catch {
        return base;
      }
    },

    async resetApplication(options: AppMaintenanceResetOptions): Promise<Result<void, string>> {
      try {
        if (options.wipeLocalSqlite) {
          try {
            await deps.localSqlite?.flushPersist();
          } catch {
            /* proceed with wipe even if flush fails */
          }
          deps.localSqlite?.clearSession();
          await deleteLocalPuntosEntregaIdb();
          clearAppMaintenanceLocalStorageKeys();
          const clearAuth = options.clearAuthSession !== false;
          if (clearAuth) {
            deps.tokenStorage?.clearToken();
            clearAuthTokenFromBrowserStorage();
          }
        }

        clearAppSessionStorageKeys();
        clearOfflineServiceWorkerUpdatePending();
        await clearBrowserOfflineShellCaches();

        if (Capacitor.isNativePlatform()) {
          try {
            await BonoNativeMaintenance.clearWebViewCache();
          } catch {
            /* WebView cache clear is best-effort */
          }
        }

        navigateToApplicationShellEntry();
        return ok(undefined);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        return fail(msg || 'No se pudo actualizar la aplicación.');
      }
    },
  };
}

/** Hard navigation to SPA shell entry so cache wipe always reloads fresh index.html. */
function navigateToApplicationShellEntry(): void {
  globalThis.location.replace(`${globalThis.location.origin}/index.html`);
}

function appConfigEnvBuildRevision(): string {
  try {
    return (import.meta.env.ADMIN_BUILD_REVISION ?? '').trim();
  } catch {
    return '';
  }
}
